//@ts-nocheck
import _ from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import L from "leaflet";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import ReactDOMServer from "react-dom/server";
import classNames from "classnames";

import { MAP_COLOR_DPE_OLD } from "constants/index";
import { CARTO_TILE_URL, CARTO_ATTRIBUTION } from "constants/map";
import API from "services/api";


function LeafletMapContent({
  searchDpesMap,
  selectedLabelId,
  dataHomeUploaded,
  onClickLabel,
  centerLat,
  centerLng,
}: any) {
  const leafletMap = useMap();

  useEffect(() => {
    if (centerLat && centerLng) {
      leafletMap.setView([centerLat, centerLng]);
    } else if (!_.isEmpty(dataHomeUploaded)) {
      leafletMap.setView([
        _.get(dataHomeUploaded, "geo_data.lat"),
        _.get(dataHomeUploaded, "geo_data.lng"),
      ]);
    }
  }, [dataHomeUploaded, leafletMap, centerLat, centerLng]);

  return (
    <>
      <TileLayer
        url={CARTO_TILE_URL}
        attribution={CARTO_ATTRIBUTION}
        maxZoom={Number(process.env.REACT_APP_MAX_ZOOM) || 20}
      />

      {!_.isEmpty(searchDpesMap) &&
        searchDpesMap.map((item: any, index: number) => {
          const customIcon = L.divIcon({
            className: "custom-div-icon",
            html: ReactDOMServer.renderToString(
              <div
                data-id={item.ademe_id}
                className={classNames(
                  "label-container min-w-[32px] min-h-[25px] rounded-3xl flex items-center justify-center",
                  {
                    "label-container--selected":
                      item.ademe_id === selectedLabelId,
                  }
                )}
                style={{
                  background: MAP_COLOR_DPE_OLD[item.label],
                }}
              >
                <span className="label-text font-medium">
                  {item.label}
                  {item.label_range?.length > 1 ? "+" : ""}
                </span>
              </div>
            ),
            iconSize: [30, 30],
          });

          return (
            <Marker
              position={item}
              key={index}
              icon={customIcon}
              zIndexOffset={item.label_z}
              eventHandlers={{
                click: () => onClickLabel(item),
              }}
            />
          );
        })}

      <Marker
        position={[
          _.get(dataHomeUploaded, "geo_data.lat"),
          _.get(dataHomeUploaded, "geo_data.lng"),
        ]}
        zIndexOffset={99999}
      />
    </>
  );
}

function NeighborsMapPage() {
  const { id: rawId } = useParams();
  const [searchParams] = useSearchParams();
  const mapRef = useRef<any>();

  // Support both proper (?lat=...) and malformed (&lat=...) URL separators.
  // When the external service uses & instead of ?, the entire suffix lands in :id.
  const ampIdx = rawId?.indexOf("&") ?? -1;
  const id = ampIdx >= 0 ? rawId!.slice(0, ampIdx) : rawId;
  const inlineParams = new URLSearchParams(ampIdx >= 0 ? rawId!.slice(ampIdx + 1) : "");

  const paramLat = (() => { const v = searchParams.get("lat") || inlineParams.get("lat"); return v ? parseFloat(v) : null; })();
  const paramLng = (() => { const v = searchParams.get("lng") || inlineParams.get("lng"); return v ? parseFloat(v) : null; })();

  const [dataHomeUploaded, setDataHomeUploaded] = useState(null);
  const [searchDpesMap, setSearchDpesMap] = useState(null);
  const [selectedLabelId, setSelectedLabelId] = useState("");

  const { mutateAsync: mutateSearchDpesMap, isPending } = useMutation({
    mutationFn: (params: any) => API.mock.searchDpesMap(params),
  });

  useEffect(() => {
    const loadData = async () => {
      const result = { geo_data: { lat: paramLat ?? 0, lng: paramLng ?? 0 } };
      setDataHomeUploaded(result);

      const resultSearchDpes = await mutateSearchDpesMap({
        ref_ademe: id,
        geometry: {
          coordinates: [result.geo_data.lng, result.geo_data.lat],
        },
        from: "neighbors-map",
      });

      if (resultSearchDpes?.center) {
        const [centerLng, centerLat] = resultSearchDpes.center;
        setDataHomeUploaded({ geo_data: { lat: centerLat, lng: centerLng } });
      }

      setSearchDpesMap(
        _.uniqBy(
          resultSearchDpes?.surrounding_addresses.map((item: any) => ({
            label: _.get(item, "label.1"),
            lat: _.get(item, "gps.1.0"),
            lng: _.get(item, "gps.1.1"),
            ademe_id: _.get(item, "ademe_id.1"),
            label_range: _.get(item, "label_range.1"),
            label_z: _.get(item, "label_z.1"),
          })),
          (item) => `${item.lat}-${item.lng}`
        )
      );
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleClickLabel = useCallback(
    async (gpsItem: any) => {
      const center = mapRef.current?.getCenter() || {};
      const resultSearchDpes = await mutateSearchDpesMap({
        ref_ademe: gpsItem.ademe_id,
        geometry: {
          coordinates: [center.lng, center.lat],
        },
        zoom: mapRef.current?.getZoom() || 18,
        from: "neighbors-map",
      });

      setSelectedLabelId(gpsItem.ademe_id);
      setSearchDpesMap(
        _.uniqBy(
          resultSearchDpes.surrounding_addresses.map((item: any) => ({
            label: _.get(item, "label.1"),
            lat: _.get(item, "gps.1.0"),
            lng: _.get(item, "gps.1.1"),
            ademe_id: _.get(item, "ademe_id.1"),
            label_range: _.get(item, "label_range.1"),
            label_z: _.get(item, "label_z.1"),
          })),
          (item) => `${item.lat}-${item.lng}`
        )
      );
    },
    [mutateSearchDpesMap]
  );

  if (isPending || !dataHomeUploaded) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <Spin indicator={<LoadingOutlined />} size="large" />
      </div>
    );
  }

  return (
    <div className="energy-home__maps" style={{ height: "100vh" }}>
      <div className="map-wrapper" style={{ marginTop: 0, height: "100%" }}>
        <MapContainer
          center={[
            paramLat ?? _.get(dataHomeUploaded, "geo_data.lat"),
            paramLng ?? _.get(dataHomeUploaded, "geo_data.lng"),
          ]}
          zoom={18}
          maxZoom={Number(process.env.REACT_APP_MAX_ZOOM) || 20}
          scrollWheelZoom={false}
          ref={mapRef}
          style={{ height: "100%", width: "100%" }}
        >
          <LeafletMapContent
            searchDpesMap={searchDpesMap}
            selectedLabelId={selectedLabelId}
            dataHomeUploaded={dataHomeUploaded}
            onClickLabel={handleClickLabel}
            centerLat={paramLat}
            centerLng={paramLng}
          />
        </MapContainer>
      </div>
    </div>
  );
}

export default NeighborsMapPage;
